import { BooleanFieldModel, LocalDateFieldModel, NumberFieldModel } from "@/common/commonForms";
import { DecisionType } from "@/common/decisions";
import { DocumentType } from "@/common/documents";
import {
  DbotEventDispatcher,
  DecisionContext,
  defaultFinancialYear,
} from "@/dbot/common/commonDomain";
import { TFunction } from "@/i18n/tt";
import { action, makeAutoObservable } from "mobx";
import { openCustomDocumentRecipientsPanelEvent } from "../customDocument/customDocumentDomain";

export type ApproveAnnualAccount = {
  type: DecisionType.approveAnnualAccount;
  isDeviatingPeriod?: boolean;
  deviatingPeriodFrom?: string;
  deviatingPeriodTo?: string;
  period?: string;
  isSmallBusinesses?: boolean;
  isGroup?: boolean;
};

export class ApproveAnnualAccountForm {
  readonly type = DecisionType.approveAnnualAccount;
  isDeviatingPeriod: BooleanFieldModel;
  deviatingPeriodFrom: LocalDateFieldModel;
  deviatingPeriodTo: LocalDateFieldModel;
  period: NumberFieldModel;
  isSmallBusinesses: BooleanFieldModel;
  isGroup: BooleanFieldModel;
  isAddingAnnualAccount?: boolean;

  constructor(
    readonly dispatch: DbotEventDispatcher,
    readonly context: DecisionContext<ApproveAnnualAccount>,
    readonly data?: ApproveAnnualAccount,
  ) {
    this.isDeviatingPeriod = new BooleanFieldModel({
      value: data?.isDeviatingPeriod,
      required: true,
    });
    this.deviatingPeriodFrom = new LocalDateFieldModel({ value: data?.deviatingPeriodFrom });
    this.deviatingPeriodTo = new LocalDateFieldModel({ value: data?.deviatingPeriodTo });
    this.period = new NumberFieldModel({
      value: Number(data?.period || defaultFinancialYear()),
      isPositiveOrZero: true,
      isInteger: true,
      disableFormatting: true,
      required: true,
    });
    this.isSmallBusinesses = new BooleanFieldModel({
      value: data?.isSmallBusinesses,
      required: true,
    });
    this.isGroup = new BooleanFieldModel({ value: data?.isGroup, required: true });
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get isProjectLocked(): boolean {
    return this.context.isLocked;
  }

  get isValid() {
    return (
      this.isDeviatingPeriod.isValid &&
      this.deviatingPeriodFrom.isValid &&
      this.deviatingPeriodTo.isValid &&
      this.period.isValid &&
      this.isSmallBusinesses.isValid &&
      this.isGroup.isValid
    );
  }

  validate(): boolean {
    this.isDeviatingPeriod.validate();
    this.isSmallBusinesses.validate();
    this.isGroup.validate();
    return this.isValid;
  }

  addAnnualAccountDocument(t: TFunction) {
    this.isAddingAnnualAccount = true;
    this.dispatch([
      {
        type: "dbot/add-documents",
        documentTypes: [DocumentType.custom_document],
        customDocumentType: "blob",
        customDocumentTitle: t("decisionType.approveAnnualAccount.add-document-tip.document-title"),
        customDocumentInitialRecipients: "boardAndCeo",
        onDone: action((_err, result) => {
          this.isAddingAnnualAccount = false;
          if (result?.addedDocumentIds.length) {
            return [
              {
                type: "dbot/toggle-document-panel",
                documentId: result.addedDocumentIds[0],
                expanded: true,
              },
              openCustomDocumentRecipientsPanelEvent(result.addedDocumentIds[0]),
            ];
          }
          return [];
        }),
      },
    ]);
  }
}
